import * as React from "react";
import { cn } from "@/lib/cn";

// ════════════════════════════════════════════════════════════════════════
// MotionChevrons — forward-motion ornament. Section dividers, footer
// accents, empty-state flourish. Always points somewhere; the last
// chevron lands on the accent color so it reads as a destination.
// ════════════════════════════════════════════════════════════════════════

export interface MotionChevronsProps extends React.SVGProps<SVGSVGElement> {
  /** Number of chevrons. Default 5. */
  count?: number;
  /** Total width (px). Default 240. */
  width?: number;
  /** Height (px). Default 80. */
  height?: number;
  /** Base color (faded chevrons). */
  color?: string;
  /** Lead color (the brightest, rightmost). */
  accent?: string;
}

export const MotionChevrons = React.forwardRef<
  SVGSVGElement,
  MotionChevronsProps
>(
  (
    {
      count = 5,
      width = 240,
      height = 80,
      color = "currentColor",
      accent = "var(--accent)",
      className,
      ...rest
    },
    ref,
  ) => {
    const items = Array.from({ length: count }, (_, i) => i);
    const step = (width - 60) / Math.max(1, count - 1);
    const cy = height / 2;
    const tall = Math.min(height, 56) * 0.4;
    return (
      <svg
        ref={ref}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className={cn("inline-block shrink-0", className)}
        aria-hidden="true"
        {...rest}
      >
        {items.map((i) => {
          const x = 20 + i * step;
          const op = 0.18 + (i / Math.max(1, count - 1)) * 0.82;
          const fill = i === count - 1 ? accent : color;
          return (
            <path
              key={i}
              d={`M${x} ${cy - tall} L${x + tall * 0.7} ${cy} L${x} ${cy + tall} L${x + tall * 0.25} ${cy} Z`}
              fill={fill}
              opacity={op}
            />
          );
        })}
      </svg>
    );
  },
);
MotionChevrons.displayName = "MotionChevrons";
